import React, { useEffect, useState } from "react";
import { Pressable } from "../../../../../pressable";
import { Loading } from "../../../../../loading";
import { usePollContext } from "../../../../../../stores/poll";
import { PollSlideType } from "../../../poll";
import { usePollResults, useVoteWithAnalytics } from "../../../../../poll";
import { ParsedText } from "../../utils/ParsedText";

export function VoteOptions({
    poll,
    pollId = null,
    pollSlideOptions,
    getLocalization,
}: {
    poll: {
        pollId: string | null;
        options: {
            text: string;
            optionId: string;
        }[];
    };
    pollId?: string | null;
    pollSlideOptions: PollSlideType["options"] | undefined;
    getLocalization: (id: string) => string;
}) {
    const pollContext = usePollContext();

    const {
        state: { voteCache, votingState },
        isClosed,
        actions: { refreshPollInfo, vote, deleteVote },
    } = pollContext;

    const { optionsWithResults, showResults } = usePollResults(
        pollId || "",
        pollSlideOptions || [],
    );
    const [votingInProgress, setVotingInProgress] = useState<boolean>(false);
    const [votingOptionId, setVotingOptionId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<boolean>(false);
    useEffect(() => {
        refreshPollInfo(pollId || "");
    }, [pollId]);
    const voteInPoll = (optionId: string[]) => vote(pollId || "", optionId);
    const voteWithAnalytics = useVoteWithAnalytics(
        pollId || "",
        pollSlideOptions || [],
        voteInPoll,
    );

    const deletingIsActive = pollId !== null && !isClosed(pollId);

    const votedForOption = (() => {
        if (
            pollId !== null &&
            pollSlideOptions !== undefined &&
            voteCache.votes[pollId] !== undefined &&
            voteCache.votes[pollId] !== null
        ) {
            const votedId = voteCache.votes[pollId]?.[0];

            const voted = pollSlideOptions.find(
                (option) => option.id === votedId,
            );
            if (!voted) return null;
            return voted.title;
        } else {
            return null;
        }
    })();

    const handleVote = async (optionTitle: string) => {
        if (!pollSlideOptions || pollId === null) return;

        const option = pollSlideOptions.find(
            (option) => option.title === optionTitle,
        );

        if (option) {
            try {
                setVotingInProgress(true);
                setVotingOptionId(optionTitle);
                await voteWithAnalytics([option.id]);
                refreshPollInfo(pollId || "");

                if (votingState === "fail") {
                    refreshPollInfo(pollId || "");
                    setVotingInProgress(false);
                    setVotingOptionId(null);
                    return;
                }
                refreshPollInfo(pollId || "");
                setVotingInProgress(false);
                setVotingOptionId(null);
            } catch (e) {
                refreshPollInfo(pollId || "");
                setVotingInProgress(false);
                setVotingOptionId(null);
                console.error("Error voting for door", e);
            }
        }
    };

    const handleDeleteVote = async (optionId: string) => {
        if (pollId === null) {
            return;
        }

        try {
            setVotingInProgress(true);
            setVotingOptionId(optionId);
            const res = await deleteVote(pollId || "", [optionId]);
            refreshPollInfo(pollId || "");

            if (res?.type === "error") {
                setDeleteError(true);
            }

            if (votingState === "fail") {
                refreshPollInfo(pollId || "");
                setVotingInProgress(false);
                setVotingOptionId(null);
                return;
            }
            refreshPollInfo(pollId || "");
            setVotingInProgress(false);
            setVotingOptionId(null);
        } catch (e) {
            setDeleteError(true);
            refreshPollInfo(pollId || "");
            setVotingInProgress(false);
            setVotingOptionId(null);
            console.error("Error deleting vote", e);
        }
    };

    const handleClick = (optionId: string) => {
        if (pollId === null) {
            return;
        }
        handleVote(optionId);
    };

    const randomlySortedPollOptions = React.useMemo(() => {
        return [...poll.options].sort(() => Math.random() - 0.5);
    }, [poll.options]);

    return (
        <>
            {(votingState === "fail" || deleteError) && (
                <div className="vote-error">
                    {getLocalization("globalText.langVoteTryAgainLabel")}
                </div>
            )}
            <div className="vote-options">
                {randomlySortedPollOptions.map((option) => {
                    const isVotedForOption = votedForOption === option.optionId;
                    const isVotingForThisOption =
                        votingOptionId === option.optionId;

                    const optionElement = (
                        <div
                            className={`vote-option ${isVotedForOption ? "voted" : ""} ${isVotingForThisOption ? "voting" : ""} ${showResults ? "show-results" : ""}`}
                            onClick={
                                votedForOption === null &&
                                !votingInProgress &&
                                !isClosed(pollId || "")
                                    ? () => handleClick(option.optionId)
                                    : undefined
                            }
                        >
                            {isVotingForThisOption && (
                                <div className="vote-option-loader">
                                    <Loading light />
                                </div>
                            )}
                            {isVotedForOption && (
                                <>
                                    <div className="voted-indicator"></div>
                                    {deletingIsActive && (
                                        <div className="remove-vote-wrapper">
                                            <Pressable>
                                                <div
                                                    className="remove-vote"
                                                    onClick={() => {
                                                        if (
                                                            !votingInProgress &&
                                                            !isClosed(
                                                                pollId || "",
                                                            )
                                                        ) {
                                                            handleDeleteVote(
                                                                option.optionId,
                                                            );
                                                        }
                                                    }}
                                                ></div>
                                            </Pressable>
                                        </div>
                                    )}
                                </>
                            )}
                            <span>
                                <ParsedText>{option.text}</ParsedText>
                            </span>
                            {showResults && (
                                <>
                                    <div className="result">
                                        {`${Math.round(
                                            (optionsWithResults.find(
                                                (o) =>
                                                    o.title === option.optionId,
                                            )?.result || 0) * 100,
                                        )}%`}
                                    </div>
                                    <div
                                        className="result-bar"
                                        style={{
                                            width: `${(optionsWithResults.find((o) => o.title === option.optionId)?.result || 0) * 100}%`,
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    );

                    if (votedForOption !== null) {
                        return (
                            <div
                                key={option.optionId}
                                className="voted-option-wrapper"
                            >
                                {optionElement}
                            </div>
                        );
                    }

                    return (
                        <Pressable key={option.optionId}>
                            {optionElement}
                        </Pressable>
                    );
                })}
            </div>
        </>
    );
}
